import RevealSection from "@/components/base/RevealSection";

const RESULTS: Record<string, {
  headline: string;
  diagnosis: string;
  meaning: string;
  helpWith: string;
  steps: string[];
}> = {
  "The Avoider": {
    headline: "You avoid difficult conversations altogether",
    diagnosis:
      "You tend to sidestep or delay workplace conversations that feel uncomfortable. Over time, this leads to unresolved issues, growing frustration, and reduced trust with colleagues.",
    meaning:
      "You value harmony and do not want to create conflict. But avoidance often makes things worse, not better. The good news: awareness is the first step to change.",
    helpWith:
      "You need help building confidence to start, and simple, low-risk scripts so you know exactly what to say without overthinking.",
    steps: [
      "Pick one small conversation you have been avoiding and set a 48-hour deadline.",
      "Use a simple script to open the conversation: 'I want to share something that has been on my mind...'",
      "Reflect on the outcome — even an imperfect conversation is progress.",
    ],
  },
  "The Hesitant Communicator": {
    headline: "You communicate, but cautiously and inconsistently",
    diagnosis:
      "You sometimes raise issues, but you soften your message or deliver it indirectly. People may not take your feedback seriously, or they may not understand what you actually need.",
    meaning:
      "You have the right instincts, but fear of hurting feelings or damaging relationships holds you back from being fully clear and direct.",
    helpWith:
      "You need structure and clarity. Scripts that help you name the issue calmly, state what you need, and close the conversation with confidence.",
    steps: [
      "Before your next conversation, write down the one specific outcome you want.",
      "Practice saying your message out loud — clarity comes from repetition.",
      "Follow up with a brief written summary so expectations are clear.",
    ],
  },
  "The Capable But Inconsistent Communicator": {
    headline: "You handle some conversations well, but not all of them",
    diagnosis:
      "You are confident in familiar situations but struggle with high-stakes, emotional, or unexpected conversations. Your skill level varies depending on the context.",
    meaning:
      "You have a solid foundation and good instincts. The inconsistency comes from lacking a repeatable framework you can rely on in any situation.",
    helpWith:
      "You need a reliable, repeatable system for any workplace conversation — boundary, feedback, conflict, escalation, or repair.",
    steps: [
      "Identify which conversation types feel hardest for you (boundary, feedback, conflict, etc.).",
      "Build a personal script library for each type so you are never starting from scratch.",
      "Set a habit of a 5-minute reflection after every difficult conversation to build consistency.",
    ],
  },
  "The Calm Authority Builder": {
    headline: "You communicate with calm authority — and can build on it",
    diagnosis:
      "You handle most difficult conversations with clarity and confidence. You are direct without being harsh, and you maintain relationships while addressing issues.",
    meaning:
      "You have strong communication skills and a solid sense of personal authority. You lead by example and model calm, clear communication for others.",
    helpWith:
      "You could benefit from refining advanced skills: coaching others, de-escalating emotionally charged situations, and scaling your approach across teams.",
    steps: [
      "Offer to mentor or coach a colleague who struggles with difficult conversations.",
      "Record yourself reflecting on a recent conversation to identify one micro-improvement.",
      "Explore how to embed calm authority into team culture and onboarding.",
    ],
  },
};

export default function QuizResults() {
  const firstName = localStorage.getItem("quiz_first_name") || "you";
  const score = parseInt(localStorage.getItem("quiz_total_score") || "0", 10);
  const category = localStorage.getItem("quiz_result_category") || "The Avoider";

  const result = RESULTS[category] || RESULTS["The Avoider"];

  return (
    <div>
      {/* Hero Result */}
      <RevealSection>
        <section className="bg-navy px-4 md:px-8 lg:px-12 pt-16 md:pt-24 pb-12 md:pb-16">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-seafoam text-sm md:text-base font-medium uppercase tracking-wider mb-4">
              Your Result
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-[48px] font-bold text-white ca-page-title mb-4">
              {category}
            </h1>
            <p className="text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
              Hi {firstName}, your score is {score}/40. {result.headline}
            </p>
          </div>
        </section>
      </RevealSection>

      {/* Diagnosis */}
      <RevealSection delay={100}>
        <section className="bg-white px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col gap-8">
              <div className="bg-seafoam/15 rounded-2xl p-6 md:p-10">
                <h2 className="text-xl md:text-2xl font-semibold text-navy mb-3">
                  What this means
                </h2>
                <p className="text-navy/70 text-base md:text-lg leading-relaxed">
                  {result.meaning}
                </p>
              </div>

              <div className="bg-white border border-seafoam/40 rounded-2xl p-6 md:p-10">
                <h2 className="text-xl md:text-2xl font-semibold text-navy mb-3">
                  Your diagnosis
                </h2>
                <p className="text-navy/70 text-base md:text-lg leading-relaxed">
                  {result.diagnosis}
                </p>
              </div>

              <div className="bg-white border border-seafoam/40 rounded-2xl p-6 md:p-10">
                <h2 className="text-xl md:text-2xl font-semibold text-navy mb-3">
                  What you need help with
                </h2>
                <p className="text-navy/70 text-base md:text-lg leading-relaxed">
                  {result.helpWith}
                </p>
              </div>
            </div>
          </div>
        </section>
      </RevealSection>

      {/* Next Steps */}
      <RevealSection delay={100}>
        <section className="bg-seafoam/15 px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-navy text-center mb-10">
              Your next 3 steps
            </h2>
            <div className="flex flex-col gap-5">
              {result.steps.map((step, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 bg-white rounded-xl p-5 md:p-6 border border-seafoam/30"
                >
                  <div className="w-10 h-10 rounded-full bg-teal flex items-center justify-center text-white font-bold text-sm flex-shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <p className="text-navy text-base md:text-lg leading-relaxed">
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </RevealSection>

      {/* CTA */}
      <RevealSection delay={100}>
        <section className="bg-white px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-navy/60 text-base md:text-lg mb-4">
              Want the exact words for your situation?
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-[36px] font-bold text-navy mb-8">
              Get the Script Pack
            </h2>
            <a
              href="https://katiahunt.gumroad.com/l/scripts"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-teal text-white font-semibold px-10 py-4 rounded-lg hover:bg-navy transition-all duration-300 text-lg whitespace-nowrap"
            >
              Get the Script Pack
            </a>
          </div>
        </section>
      </RevealSection>

      {/* Course CTA */}
      <RevealSection delay={100}>
        <section className="bg-navy px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Difficult Conversations Without Damage
            </h2>
            <p className="text-white/60 text-base md:text-lg leading-relaxed mb-6 max-w-xl mx-auto">
              A complete course to help you master workplace conversations with clarity, calm and confidence.
            </p>
            <a
              href="https://katiahunt.gumroad.com/l/course"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center border-2 border-seafoam text-seafoam font-semibold px-8 py-3 rounded-lg hover:bg-seafoam hover:text-navy transition-all text-sm whitespace-nowrap"
            >
              Get the Course
            </a>
          </div>
        </section>
      </RevealSection>
    </div>
  );
}